import {
  abTestsAddonPrices,
  fullAgencyEventTrackerPrices,
  fullECommercePrices,
  popupsPrices,
  smartSectionsPrices
} from '@constants/addonsPrices'
import { SelectableBillingPeriodTypes } from '@constants/billingPeriods'
import { CurrencyType } from '@constants/currencies'
import {
  AGENCY_PLANS,
  defaultAutomatePlan,
  defaultCorePlan,
  defaultCreatePlan,
  defaultEnterprisePlan,
  defaultOldAgencyPlan,
  PlanRecordKeyTypes,
  PROFESSIONAL_PLANS
} from '@constants/plans'
import ABTests from '@images/addons/icons/ABTests.svg'
import ECommerce from '@images/addons/icons/ECommerce.svg'
import EventTracker from '@images/addons/icons/EventTracker.svg'
import SmartSections from '@images/addons/icons/icon_smart_section.svg'
import MultiLanguage from '@images/addons/icons/MultiLanguage.svg'
import Popups from '@images/addons/icons/Popups.svg'
import ProgrammaticLP from '@images/addons/icons/ProgrammaticLP.svg'
import { ReactNode } from 'react'
import { Trans } from 'react-i18next'

interface AddonType {
  icon: string
  name: ReactNode
  tooltip: ReactNode
  price?: number
  availableOnPlans: PlanRecordKeyTypes[]
}

export const useGetPlanAddons = ({
  recordKey,
  currency,
  billingPeriod
}: {
  recordKey: PlanRecordKeyTypes
  currency: CurrencyType
  billingPeriod: SelectableBillingPeriodTypes
}) => {
  const addons: AddonType[] = [
    {
      icon: Popups,
      name: <Trans i18nKey='addons.feature.popups' />,
      tooltip: <Trans i18nKey='addons.feature.popups.tooltip' />,
      price: popupsPrices[billingPeriod][currency],
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultCreatePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: SmartSections,
      name: <Trans i18nKey='addons.feature.smart.sections' />,
      tooltip: <Trans i18nKey='addons.feature.smart.sections.tooltip' />,
      price: smartSectionsPrices[billingPeriod][currency],
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: ABTests,
      name: <Trans i18nKey='addons.feature.ab.tests' />,
      tooltip: <Trans i18nKey='addons.feature.ab.tests.tooltip' />,
      price: abTestsAddonPrices[billingPeriod][currency],
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: ECommerce,
      name: <Trans i18nKey='addons.feature.ecommerce.hub' />,
      tooltip: <Trans i18nKey='addons.feature.ecommerce.hub.tooltip' />,
      price: fullECommercePrices[billingPeriod][currency],
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultCorePlan,
        defaultCreatePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: ProgrammaticLP,
      name: <Trans i18nKey='addons.feature.programmatic.lp' />,
      tooltip: <Trans i18nKey='addons.feature.programmatic.lp.tooltip' />,
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultCorePlan,
        defaultCreatePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: MultiLanguage,
      name: <Trans i18nKey='addons.feature.ai.multi.language' />,
      tooltip: <Trans i18nKey='addons.feature.ai.multi.language.tooltip' />,
      availableOnPlans: [
        ...PROFESSIONAL_PLANS,
        ...AGENCY_PLANS,
        defaultEnterprisePlan,
        defaultAutomatePlan,
        defaultOldAgencyPlan
      ]
    },
    {
      icon: EventTracker,
      name: <Trans i18nKey='compare.plans.description.event.tracker' />,
      tooltip: (
        <Trans i18nKey='compare.plans.description.event.tracker.tooltip' />
      ),
      price: fullAgencyEventTrackerPrices[billingPeriod][currency],
      availableOnPlans: [defaultEnterprisePlan]
    }
  ]

  const availableAddonsForPlan = addons.filter(addon =>
    addon.availableOnPlans.includes(recordKey)
  )

  const totalPrice = availableAddonsForPlan.length
    ? availableAddonsForPlan.reduce(
        (sum, addon) => sum + (addon?.price ? addon.price : 0),
        0
      )
    : null

  return {
    availableAddonsForPlan,
    totalPrice
  }
}
